import { pipe } from 'fp-ts/function';
import { array, number, ord } from 'fp-ts';
import { get, map, memoize } from 'lodash/fp';
import { CustomerClassifications } from '@mews-commander/legacy-data-structures';
import { Customer, ResourceBlockType, SexEnum } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { localizeText, customerClassificationIconNameHandler, getReversedFullName } from '@mews-commander/utils';
import { ResourceUseResource } from '@mews-commander/legacy-entities/src/ducks/resourcesTree';
import { ICON, LABEL_PART } from '../../constants';
import { advancedReturningClassificationIconNameHandler } from '../../../../accounts/utils';

const memoCustomerClassificationIconName = memoize(customerClassificationIconNameHandler);
const memoAdvancedReturningClassificationIconName = memoize(advancedReturningClassificationIconNameHandler);

interface GetReservationLabelPartsParams {
    assignedResourceLocked: boolean;
    requestedCategoryId: string;
    resource: ResourceUseResource;
    customer: Customer;
    adultCount: number;
    childCount: number;
    totalStays: number;
}

const getReservationLabelParts = ({
    assignedResourceLocked,
    requestedCategoryId,
    resource,
    customer,
    adultCount,
    childCount,
    totalStays,
}: GetReservationLabelPartsParams) => {
    const { normalBedCount, extraBedCount, resourceCategoryId } = resource;

    const labelParts = [];
    if (assignedResourceLocked) {
        labelParts.push({ type: LABEL_PART.ICON, value: ICON.LOCK });
    }
    if (requestedCategoryId !== resourceCategoryId) {
        labelParts.push({ type: LABEL_PART.ICON, value: ICON.UPGRADE });
    }
    if (customer.gender === SexEnum.Female && adultCount + childCount === 1 && normalBedCount + extraBedCount === 1) {
        labelParts.push({ type: LABEL_PART.ICON, value: ICON.FEMALE });
    }

    labelParts.push(...getCustomerClassificationLabelParts(customer, totalStays));

    if (totalStays) {
        labelParts.push({
            type: LABEL_PART.CUSTOMER_CLASSIFICATION,
            value: memoAdvancedReturningClassificationIconName(totalStays),
        });
    }

    labelParts.push({ type: LABEL_PART.TEXT, value: getReversedFullName(customer) });

    return labelParts;
};

const getSpaceBlockLabelParts = ({ type, name }: $TSFixMe) => {
    const labelParts = [];
    if (type === ResourceBlockType.InternalUse) {
        labelParts.push({ type: LABEL_PART.ICON, value: ICON.HOUSE_USE });
    }
    if (type === ResourceBlockType.OutOfOrder) {
        labelParts.push({ type: LABEL_PART.ICON, value: ICON.OUT_OF_ORDER });
    }
    labelParts.push({ type: LABEL_PART.TEXT, value: name });

    return labelParts;
};

const getOtherServiceUseLabelParts = ({ serviceName }: $TSFixMe) => [{ type: LABEL_PART.TEXT, value: serviceName }];
const getMultiServiceUseLabelParts = () => [{ type: LABEL_PART.TEXT, value: localizeText(TextKey.MultipleServices) }];

const getCustomerClassificationLabelParts = (customer: Customer, totalStays: number | null) =>
    pipe(
        customer,
        get('classificationsDecoded'),
        array.sort(
            ord.contramap(({ value }: { value: number }) =>
                value === CustomerClassifications.Returning.value ? 1 : 0
            )(number.Ord)
        ),
        array.filter(({ value }) => !(totalStays != null && value === CustomerClassifications.Returning.value)),
        map(({ value }) => ({
            type: LABEL_PART.CUSTOMER_CLASSIFICATION,
            value: memoCustomerClassificationIconName(value),
        }))
    );

export const getLabelParts = ({ isReservation, isOtherServiceUse, use }: $TSFixMe) => {
    if (!isReservation) {
        return getSpaceBlockLabelParts(use);
    }
    if (use.isMultipleServices) {
        // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
        return getMultiServiceUseLabelParts(use);
    }
    if (isOtherServiceUse) {
        return getOtherServiceUseLabelParts(use);
    }
    return getReservationLabelParts(use);
};
